import type { SiteStatus, InvoiceSubmitChannel, ContractStatus, InvoiceStatus } from "#imports"
import { Scope } from "#imports"

// Mirrors the Meilisearch documents built by `zefire/logic/globalsearch.py` (backend repository).

interface _DeliveryPointRecord {
  prm: string | null
  card: string | null
}

interface _EntityRecord {
  uuid: string
  name: string
}

interface _ContractPartyRecord extends _EntityRecord {
  logo: string | null
}

interface _ContractSiteRecord extends _EntityRecord {
  machine_type: string | null
  is_external: boolean
}

export interface SiteGlobalSearchResult extends _EntityRecord {
  type: "site"
  external_id: string | null
  accounting_id: string | null
  siret: string | null
  deliverypoints: _DeliveryPointRecord[]
  machine_type: string | null
  capacity: number
  is_external: boolean
  status: SiteStatus
  assetowner: _EntityRecord | null
  holding: _EntityRecord | null
}

interface _CompanyGlobalSearchResult extends _EntityRecord {
  name_short: string | null
  registration_number: string | null
  vat_number: string | null
  registration_office: string | null
  n_sites: number
  capacity: number
}

export interface HoldingGlobalSearchResult extends _CompanyGlobalSearchResult {
  type: "holding"
  n_assetowners: number
}

export interface AssetOwnerGlobalSearchResult extends _CompanyGlobalSearchResult {
  type: "assetowner"
  holding: { name: string } | null
}

export type CompanyGlobalSearchResult = HoldingGlobalSearchResult | AssetOwnerGlobalSearchResult

export interface PortfolioGlobalSearchResult extends _EntityRecord {
  type: "portfolio"
  n_sites: number
  capacity: number
}

export interface ContractGlobalSearchResult {
  type: "contract"
  // Ranking tie-breaker: contracts (0) rank before invoices (1) on equal relevance.
  invoice_priority: 0
  uuid: string
  purchase_order: string | null
  contract_type: string
  external_id: string | null
  external_name: string | null
  start: string
  end: string
  order_number: string | null
  market_number: string | null
  invoice_submit_channel: InvoiceSubmitChannel
  current_status: ContractStatus
  clause_types: string[]
  sites: (_ContractSiteRecord & { external_id: string | null })[]
  deliverypoints: _DeliveryPointRecord[]
  counterpart: _ContractPartyRecord
  managed_party: _ContractPartyRecord
  // Only set when the managed party is an asset owner.
  assetowner: _EntityRecord | null
  holding: _EntityRecord | null
}

export interface InvoiceGlobalSearchResult {
  type: "invoice"
  invoice_priority: 1
  uuid: string
  start: string
  end: string
  publication_date: string | null
  publication_id: string | null
  external_publication_id: string | null
  status: InvoiceStatus
  is_birthday: boolean
  is_only_credit_note: boolean
  has_credit_note_lines: boolean
  is_accounted: boolean
  total_line: {
    quantity: number
    price: number
    amount: number
  } | null
  sites: _ContractSiteRecord[]
  contract: {
    type: string
    purchase_order: string | null
  }
}

type BaseSearchResult<T> = T & {
  _formatted: T
  _matchesPosition: Record<string, { start: number; length: number }[]>
}
export type GlobalSearchResult =
  | BaseSearchResult<SiteGlobalSearchResult>
  | BaseSearchResult<ContractGlobalSearchResult>
  | BaseSearchResult<InvoiceGlobalSearchResult>
  | BaseSearchResult<CompanyGlobalSearchResult>
  | BaseSearchResult<PortfolioGlobalSearchResult>

export interface GlobalSearchSelectOpts {
  isMeta?: boolean
  // tab: string // todo
}

const ESCAPE_HTML_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;",
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ESCAPE_HTML_MAP[char] ?? char)
}

/**
 * Meilisearch `_formatted` fields wrap matches in `<em>` tags but do NOT escape the
 * underlying value, which is user-authored (site names, company names, ...). Escape
 * everything, then restore only the inert `<em>` highlight tags so the result is
 * safe to render via v-html.
 */
export function sanitizeMeiliHighlight(value: string | undefined): string {
  if (!value) return ""
  return escapeHtml(value).replaceAll("&lt;em&gt;", "<em>").replaceAll("&lt;/em&gt;", "</em>")
}

// Scope required to see the records of a type: the tenant token served by `/api/auth/me` filters
// them out server-side otherwise, so filtering on that type would always come back empty.
const GLOBAL_SEARCH_TYPE_SCOPES: Partial<Record<string, Scope>> = {
  contract: Scope.enum["read:contract"],
  invoice: Scope.enum["read:invoice"],
}

export function isGlobalSearchTypeAllowed(type: string): boolean {
  const scope = GLOBAL_SEARCH_TYPE_SCOPES[type]

  return R.isNullish(scope) || hasScope(scope)
}

export const mapGlobSearchTypeIcons = {
  portfolio: $icons.model.portfolio,
  site: $icons.model.site,
  assetowner: $icons.model.assetowner,
  assetmanager: $icons.model.assetmanager,
  contract: $icons.model.contract,
  holding: $icons.model.holding,
  invoice: $icons.model.invoice,
} as const
